import React from 'react';
import { motion } from 'framer-motion';

/**
 * CoachYumixAvatar
 * ----------------
 * Premium visual signature for the "Coach YUMIX" AI persona.
 *
 * Two illustrations are bundled:
 *   - `portrait`  → close-up bust (orange suit, pointing gesture)
 *                   ideal for chat headers, message bubbles, greeting cards.
 *   - `hero`      → full body (red & gold suit, full pose)
 *                   ideal for loading screens, paywalls, hero placements.
 *
 * Visual style — Yuri spec Iter. 406.fr.10 :
 *   • Round shape (Q1=C) with breathing gradient halo (Q3=C)
 *   • Adaptive sizing: xs (28) / sm (36) / md (48) / lg (72) / xl (120)
 *   • Animated radial glow that respects `prefers-reduced-motion`
 */
export const CoachYumixAvatar = ({
  variant = 'portrait',
  size = 'md',
  animate = true,
  className = '',
  glowColor = 'orange',
  ...rest
}) => {
  const sizeMap = {
    xs: { box: 28, ring: 1, offset: 0 },
    sm: { box: 36, ring: 1, offset: 1 },
    md: { box: 48, ring: 2, offset: 1 },
    lg: { box: 72, ring: 2, offset: 2 },
    xl: { box: 120, ring: 3, offset: 3 },
  };
  const { box, ring, offset } = sizeMap[size] || sizeMap.md;

  const src =
    variant === 'hero'
      ? '/assets/coach-yumix-avatar-1.png'
      : '/assets/coach-yumix-avatar-2.png';

  const haloPalette = {
    orange: {
      base: 'rgba(251,146,60,0.55)',
      mid: 'rgba(244,63,94,0.35)',
      outer: 'rgba(168,85,247,0.25)',
      ring: 'rgba(251,191,36,0.65)',
    },
    purple: {
      base: 'rgba(168,85,247,0.55)',
      mid: 'rgba(232,121,249,0.35)',
      outer: 'rgba(56,189,248,0.20)',
      ring: 'rgba(216,180,254,0.65)',
    },
    emerald: {
      base: 'rgba(16,185,129,0.55)',
      mid: 'rgba(45,212,191,0.35)',
      outer: 'rgba(132,204,22,0.20)',
      ring: 'rgba(110,231,183,0.65)',
    },
  };
  const palette = haloPalette[glowColor] || haloPalette.orange;

  const pulse = animate
    ? {
        animate: {
          boxShadow: [
            `0 0 ${box * 0.18}px ${palette.base}, 0 0 ${box * 0.32}px ${palette.mid}, inset 0 0 ${offset + 4}px ${palette.outer}`,
            `0 0 ${box * 0.30}px ${palette.base}, 0 0 ${box * 0.55}px ${palette.mid}, inset 0 0 ${offset + 6}px ${palette.outer}`,
            `0 0 ${box * 0.18}px ${palette.base}, 0 0 ${box * 0.32}px ${palette.mid}, inset 0 0 ${offset + 4}px ${palette.outer}`,
          ],
        },
        transition: { duration: 3.2, repeat: Infinity, ease: 'easeInOut' },
      }
    : {};

  return (
    <motion.div
      className={`relative inline-flex items-center justify-center rounded-full overflow-hidden shrink-0 ${className}`}
      style={{
        width: box,
        height: box,
        background: `radial-gradient(circle at 30% 25%, ${palette.base}, ${palette.outer} 70%, transparent 100%)`,
        border: `${ring}px solid ${palette.ring}`,
      }}
      {...pulse}
      data-testid="coach-yumix-avatar"
      data-variant={variant}
      data-size={size}
      {...rest}
    >
      <img
        src={src}
        alt="Coach YUMIX"
        className="w-full h-full object-cover"
        style={{
          objectPosition: variant === 'hero' ? 'center 18%' : 'center 22%',
          transform: variant === 'hero' ? 'scale(1.08)' : 'scale(1.12)',
        }}
        loading="lazy"
        draggable={false}
        onError={(e) => {
          // Graceful fallback – hide broken image, keep glow halo visible
          e.currentTarget.style.display = 'none';
        }}
      />
    </motion.div>
  );
};

export default CoachYumixAvatar;
